/**
 * CORS-safe fetch for Tauri desktop app.
 *
 * In Tauri, browser fetch is blocked by CORS (origin: http://tauri.localhost).
 * This module uses @tauri-apps/plugin-http which makes requests from the Rust
 * side, completely bypassing CORS. In web mode, it falls back to native fetch.
 *
 * Also resolves the correct API base URL:
 * - Web: /api/backend (Next.js rewrite proxy)
 * - Tauri: https://{host}/api/backend (direct, from localStorage)
 */

const isTauri = typeof window !== "undefined" && "__TAURI__" in window;

let tauriFetchFn: typeof globalThis.fetch | null = null;

/**
 * Drop-in replacement for fetch() that works in both web and Tauri.
 * Automatically resolves /api/backend/* URLs to the correct Tauri base.
 */
export async function safeFetch(
  input: string | URL | Request,
  init?: RequestInit,
): Promise<Response> {
  let url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

  if (isTauri) {
    // Resolve relative /api/backend/* URLs to the saved absolute URL
    if (url.startsWith("/api/backend")) {
      const saved = localStorage.getItem("efakt-api-url") || "";
      if (saved) {
        url = url.replace("/api/backend", saved);
      }
    }

    if (!tauriFetchFn) {
      const mod = await import("@tauri-apps/plugin-http");
      tauriFetchFn = mod.fetch as typeof globalThis.fetch;
    }
    return tauriFetchFn(url, init);
  }

  return fetch(url, init);
}
